import { buildActivityLogDetailRows } from "./activityLogDetail";

const pickDisplayValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text =
      typeof value === "number" ? String(value) : String(value).trim();
    if (text) return text;
  }
  return "";
};

export const NC_ACTIVITY_APPLICATION_DETAIL_FIELDS = [
  { key: "applicationId", label: "Application Id" },
  { key: "name", label: "Applicant Name" },
  { key: "appliedLoad", label: "Applied Load(kW)" },
  { key: "tariff", label: "Tariff" },
];

export function buildNcActivityLogApplicationDetails(
  rowData = {},
  { tariff: tariffFromView } = {},
) {
  return {
    applicationId: rowData.applicationId || "",
    name: pickDisplayValue(rowData.applicantName, rowData.consumerName),
    appliedLoad: pickDisplayValue(rowData.appliedLoad),
    tariff: pickDisplayValue(
      tariffFromView,
      rowData.tariff,
      rowData.tariffDesc,
      rowData.tariffDescription,
    ),
  };
}

const hasObjection = (status) => Number(status) === 1;

export function resolveNcHistoryStepName(item = {}) {
  const x = item.applicationProgressId;
  const y = item.remarks;
  const objectionStatus = item.objectionStatus;

  if (x === 0) {
    return "Application Submitted";
  }
  if (x === 1) {
    if (y && y.includes("Reuploaded")) {
      return "Documents Reupload";
    }
    return hasObjection(objectionStatus)
      ? "Documents Objection"
      : "Application Submitted";
  }
  if (x === 21) {
    if (y && y.includes("Reupload")) {
      return "Wiring Test Report Reupload";
    }
    return hasObjection(objectionStatus)
      ? "Wiring Test Report Rejected"
      : "Documents Verified";
  }
  if (x === 2) {
    if (y && y.includes("exist")) {
      return "Arrear Objection";
    }
    if (y && y.includes("Removed")) {
      return "Arrear Objection Removed";
    }
    return hasObjection(objectionStatus)
      ? "Arrear Objection"
      : "Wiring Test Report Verification";
  }
  if (x === 3 || (x === 3 && y === "Arrears Verified.")) {
    return "Arrear Verification";
  }
  if (x === 4) {
    return "Field Survey";
  }
  if (x === 5 && y === "Reference No. allocated.") {
    return "Reference No. Allocated";
  }
  if (
    (x === 5 && y === "Demand Note Approved.") ||
    (x === 5 && y === "Demand Note Issued.") ||
    (x === 5 && y === "Demand Notice Created")
  ) {
    return "Demand Note Approved";
  }
  if (x === 6) {
    return "DN Payment Submission";
  }
  if (x === 7) {
    return "DN Payment Verification";
  }
  if (x === 8 && y === "DN Payment Verified") {
    return "DN Payment Verification";
  }
  if (x === 8) {
    return "Service Connection Order";
  }
  if (x === 9) {
    return "Meter Installation";
  }
  if (x === 10) {
    return "Ready for Billing";
  }

  return item.ncProgStatusName || "-";
}

export const NC_ACTIVITY_STEPS = [
  {
    id: 1,
    label: "Document Submission",
    slaProgressId: 0,
    slaProgIdName: "Document Submission",
    slaDays: 0,
    aliases: ["Application Submitted"],
  },
  {
    id: 2,
    label: "Document Verification",
    slaProgressId: 1,
    slaProgIdName: "Document Verification",
    slaDays: 1,
    aliases: [
      "Documents Verified",
      "Documents Objection",
      "Documents Reupload",
    ],
  },
  {
    id: 3,
    label: "Wiring Test Report",
    slaDays: 1,
    aliases: [
      "Wiring Test Report Verification",
      "Wiring Test Report Reupload",
      "Wiring Test Report Rejected",
    ],
  },
  {
    id: 4,
    label: "Arrear Verification",
    slaProgressId: 2,
    slaProgIdName: "Arrear Verification",
    slaDays: 1,
    aliases: ["Arrear Objection", "Arrear Objection Removed"],
  },
  {
    id: 5,
    label: "Field Survey & Cost Estimation",
    slaProgressId: 3,
    slaProgIdName: "Field Survey",
    slaDays: 1,
    aliases: ["Field Survey"],
  },
  {
    id: 6,
    label: "Reference No. Allocation",
    slaProgressId: 4,
    slaProgIdName: "Ref. No. Allocation",
    slaDays: 1,
    aliases: ["Reference No. Allocated"],
  },
  {
    id: 7,
    label: "DN Approval",
    slaDays: 1,
    aliases: ["Demand Note Approved"],
  },
  {
    id: 8,
    label: "DN Submission",
    slaProgressId: 5,
    slaProgIdName: "Demand Note Payment",
    slaDays: 365,
    aliases: ["DN Payment Submission"],
  },
  {
    id: 9,
    label: "DN Payment Verification",
    slaProgressId: 6,
    slaProgIdName: "Demand Note Payment Verification",
    slaDays: 1,
  },
  {
    id: 10,
    label: "Service Connection Order",
    slaProgressId: 7,
    slaProgIdName: "SCO",
    slaDays: 0,
  },
  {
    id: 11,
    label: "Meter Installation",
    slaProgressId: 8,
    slaProgIdName: "Meter Installation",
    slaDays: 1,
  },
  {
    id: 12,
    label: "Ready For Billing",
    slaProgressId: 9,
    slaProgIdName: "Ready for Billing",
    slaDays: 1,
    aliases: ["Ready for Billing"],
  },
];

function toFiniteNumber(value) {
  if (value == null || value === "") return NaN;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : NaN;
  }
  const matched = String(value).replace(/,/g, "").match(/-?\d+(\.\d+)?/);
  if (!matched) return NaN;
  const parsed = Number(matched[0]);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function normalizeProgName(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function findNcTimeFrameForLoad(
  timeFrameData = [],
  sanctionedLoad,
  progressId,
  progIdName,
) {
  const load = toFiniteNumber(sanctionedLoad);
  const pid = toFiniteNumber(progressId);
  const wantedName = normalizeProgName(progIdName);

  if (
    !Array.isArray(timeFrameData) ||
    Number.isNaN(load) ||
    (Number.isNaN(pid) && !wantedName)
  ) {
    return null;
  }

  const matches = timeFrameData.filter((frame) => {
    const loadFrom = toFiniteNumber(frame.loadFrom);
    const loadTo = toFiniteNumber(frame.loadTo);
    if (Number.isNaN(loadFrom) || Number.isNaN(loadTo)) return false;
    if (load < loadFrom || load > loadTo) return false;

    const framePid = toFiniteNumber(frame.progressId);
    const pidMatch = !Number.isNaN(pid) && framePid === pid;
    const nameMatch =
      Boolean(wantedName) &&
      normalizeProgName(frame.progIdName) === wantedName;

    if (!Number.isNaN(pid) && wantedName) return pidMatch || nameMatch;
    if (!Number.isNaN(pid)) return pidMatch;
    return nameMatch;
  });

  if (matches.length === 0) return null;

  return matches.reduce((best, frame) => {
    const span =
      toFiniteNumber(frame.loadTo) - toFiniteNumber(frame.loadFrom);
    const bestSpan =
      toFiniteNumber(best.loadTo) - toFiniteNumber(best.loadFrom);
    if (span !== bestSpan) return span < bestSpan ? frame : best;
    if (
      wantedName &&
      normalizeProgName(frame.progIdName) === wantedName &&
      normalizeProgName(best.progIdName) !== wantedName
    ) {
      return frame;
    }
    return best;
  });
}

export function buildNcActivityStepsForLoad(
  timeFrameData = [],
  sanctionedLoad,
) {
  return NC_ACTIVITY_STEPS.map((step) => {
    if (step.label === "Document Submission") {
      return step;
    }

    const frame = findNcTimeFrameForLoad(
      timeFrameData,
      sanctionedLoad,
      step.slaProgressId,
      step.slaProgIdName,
    );

    if (!frame) return step;

    const slaDays = toFiniteNumber(frame.allowedNoOfDays);
    if (Number.isNaN(slaDays)) return step;

    return {
      ...step,
      slaDays,
    };
  });
}

export function findNcStepDefForCurrentProgress(
  progressId,
  steps = NC_ACTIVITY_STEPS,
) {
  const pid = Number(progressId);
  if (!Number.isFinite(pid)) return null;

  if (pid === 7 || pid === 8) {
    return steps.find((step) => step.label === "Meter Installation") ?? null;
  }
  if (pid === 9 || pid === 10) {
    return steps.find((step) => step.label === "Ready For Billing") ?? null;
  }

  const bySla = steps.find(
    (step) => toFiniteNumber(step.slaProgressId) === pid,
  );
  if (bySla) return bySla;

  return steps.find((step) => step.id === pid + 1) ?? null;
}

export function resolveNcTimelineCurrentStepName(
  progressId,
  _currentStatus = {},
  steps = NC_ACTIVITY_STEPS,
) {
  return findNcStepDefForCurrentProgress(progressId, steps)?.label || "-";
}

export function shouldNcAdvancePastHistoryMatch(historyItem, progressId) {
  const remark = String(historyItem?.remarks || "").trim();
  const historyProgressId = Number(historyItem?.applicationProgressId);
  const pid = Number(progressId);

  if (
    historyProgressId === 8 &&
    remark === "DN Payment Verified" &&
    (pid === 7 || pid === 8)
  ) {
    return true;
  }

  if (
    historyProgressId === 9 &&
    (remark === "Meter Installed" ||
      remark === "Meter Installed on premises." ||
      remark === "Meter Installation Approved.") &&
    pid === 9
  ) {
    return true;
  }

  return false;
}

export function buildNcActivityLogDetailRows(
  historyItems = [],
  currentStatus = {},
  formatDateFn,
  options = {},
) {
  const { timeFrameData, sanctionedLoad, appliedLoad } = options;
  const loadValue =
    appliedLoad ??
    sanctionedLoad ??
    currentStatus.appliedLoad ??
    currentStatus.sanctionedLoad;
  const steps =
    Array.isArray(timeFrameData) &&
    timeFrameData.length > 0 &&
    loadValue != null &&
    loadValue !== ""
      ? buildNcActivityStepsForLoad(timeFrameData, loadValue)
      : NC_ACTIVITY_STEPS;

  const enriched = historyItems.map((item) => ({
    ...item,
    ncProgStatusName: resolveNcHistoryStepName(item),
  }));

  return buildActivityLogDetailRows(enriched, currentStatus, formatDateFn, {
    steps,
    stepNameField: "ncProgStatusName",
    totalExcludeStepLabels: ["DN Submission", "DN Payment Submission"],
    findStepDefByProgressIdFn: (progressId, stepDefs) =>
      findNcStepDefForCurrentProgress(progressId, stepDefs),
    resolveCurrentStepName: (progressId, currentStatus, stepDefs) =>
      resolveNcTimelineCurrentStepName(progressId, currentStatus, stepDefs),
    shouldAdvancePastHistoryMatch: shouldNcAdvancePastHistoryMatch,
  });
}
